import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon as SearchIcon,
  TrashIcon,
  ArrowDownTrayIcon as DownloadIcon,
  ArrowPathIcon as RefreshIcon,
  ClockIcon,
  FunnelIcon,
  XMarkIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import {
  getChatHistory,
  deleteChatHistoryItem,
  clearChatHistory,
  exportChatHistory,
  getChatHistoryStats,
} from '../services/chatHistoryService';

// Custom Confirmation Dialog Component (Tailwind + Heroicons)
function TeamsShareConfirmDialog({ open, onClose, onConfirm }) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000]" onClick={onClose} />
      <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center">
              <ShareIcon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Share to Microsoft Teams</h3>
          </div>
          
          <div className="mb-6 space-y-3">
            <p className="text-gray-700 font-semibold flex items-center gap-2">
              <span className="text-2xl">✅</span>
              <span>FULL MESSAGE COPIED TO CLIPBOARD!</span>
            </p>
            <p className="text-gray-600 text-sm">
              📋 The complete Q&A has been copied to your clipboard.
            </p>
            
            <div className="bg-purple-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-purple-900 mb-2">📌 Next steps:</p>
              <ol className="text-sm text-purple-800 space-y-1 list-decimal list-inside">
                <li>Click &quot;Open Teams&quot; to open Microsoft Teams</li>
                <li>Select a chat or channel in Teams</li>
                <li>Paste the message (Ctrl+V or Cmd+V)</li>
                <li>Send!</li>
              </ol>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-medium hover:from-purple-600 hover:to-purple-800 transition-all shadow-lg hover:shadow-xl"
            >
              Open Teams
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default function ChatHistorySection({ onQuestionSelect }) {
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [stats, setStats] = useState(null);
  const [showTeamsDialog, setShowTeamsDialog] = useState(false);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = () => {
    const historyData = getChatHistory();
    setHistory(historyData);
    setStats(getChatHistoryStats());
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item from history?')) {
      deleteChatHistoryItem(id);
      loadHistory();
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all history? This action cannot be undone.')) {
      clearChatHistory();
      loadHistory();
    }
  };

  const handleExport = () => {
    const jsonData = exportChatHistory();
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-history-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleUseQuestion = (question) => {
    onQuestionSelect(question);
  };

  const handleShareToTeams = (item) => {
    // Clean markdown from answer for better Teams display
    const cleanAnswer = item.answer
      .replace(/```[\s\S]*?```/g, (match) => match.replace(/```/g, ''))
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '');
    
    const deviceInfo = item.deviceSerial ? `Device: ${item.deviceSerial}\n\n` : '';
    const category = item.category ? `Category: ${item.category}\n` : '';
    const timestamp = formatDate(item.timestamp);
    
    const message = `📋 IOT-A Chat - Q&A\n\n` +
                   `⏰ ${timestamp}\n` +
                   `${category}` +
                   `${deviceInfo}` +
                   `❓ Question:\n${item.question}\n\n` +
                   `✅ Answer:\n${cleanAnswer}`;
    
    console.log('📤 Sharing to Teams from history - Message length:', message.length);
    
    navigator.clipboard.writeText(message).then(() => {
      console.log('✅ Message copied to clipboard');
      setShowTeamsDialog(true);
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
      alert('❌ Failed to copy message to clipboard. Please try again.');
    });
  };

  const handleTeamsDialogConfirm = () => {
    window.open('https://teams.microsoft.com/', '_blank');
    setShowTeamsDialog(false);
  };

  // Filter and sort history
  const getFilteredAndSortedHistory = () => {
    let filtered = [...history];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.question.toLowerCase().includes(term) ||
        item.answer.toLowerCase().includes(term) ||
        (item.deviceSerial && item.deviceSerial.toLowerCase().includes(term))
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.timestamp) - new Date(a.timestamp);
        case 'date-asc':
          return new Date(a.timestamp) - new Date(b.timestamp);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const filteredHistory = getFilteredAndSortedHistory();
  const categories = stats ? Object.keys(stats.categories) : [];

  return (
    <>
      <div>
        {/* Header with Stats */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold flex items-center gap-2 text-sm">
              <ClockIcon className="h-5 w-5" />
              Conversation History
            </h3>
            <div className="flex gap-2">
              <button
                onClick={loadHistory}
                className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                title="Refresh"
              >
                <RefreshIcon className="h-4 w-4" />
              </button>
              <button
                onClick={handleExport}
                disabled={history.length === 0}
                className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors disabled:opacity-50"
                title="Export history"
              >
                <DownloadIcon className="h-4 w-4" />
              </button>
              <button
                onClick={handleClearAll}
                disabled={history.length === 0}
                className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors disabled:opacity-50"
                title="Clear all"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Stats Chips */}
          {stats && stats.totalQuestions > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-1 rounded">
                {stats.totalQuestions} questions
              </span>
              <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-1 rounded">
                {Object.keys(stats.categories).length} categories
              </span>
              {stats.devices && Object.keys(stats.devices).length > 0 && (
                <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-1 rounded">
                  {Object.keys(stats.devices).length} devices
                </span>
              )}
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="mb-4">
          {/* Search Bar */}
          <div className="mb-3 relative">
            <SearchIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-purple-600" />
            <input
              type="text"
              placeholder="Search in history..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white rounded-lg pl-10 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <FunnelIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full bg-white text-gray-800 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)} ({stats.categories[cat]})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-white text-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
              >
                <option value="date-desc">Most recent</option>
                <option value="date-asc">Oldest</option>
                <option value="category">Category</option>
              </select>
            </div>
          </div>
        </div>

        <div className="h-px bg-white bg-opacity-20 mb-3" />

        {/* History List */}
        <div className="max-h-[400px] overflow-y-auto">
          {filteredHistory.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-white text-opacity-70">
                {history.length === 0 
                  ? '📝 No history yet. Ask some questions to get started.'
                  : '🔍 No results found with the applied filters.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredHistory.map((item) => (
                <div
                  key={item.id}
                  className="bg-white bg-opacity-10 rounded-lg border border-white border-opacity-20 p-3 transition-all hover:bg-opacity-15 hover:border-opacity-30"
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="text-white text-opacity-60 text-xs mb-1">
                        {formatDate(item.timestamp)}
                      </p>
                      <div className="flex gap-2 flex-wrap mt-1">
                        <span className="bg-purple-600 bg-opacity-80 text-white text-xs px-2 py-0.5 rounded">
                          {item.category}
                        </span>
                        {item.deviceSerial && (
                          <span className="bg-green-500 bg-opacity-80 text-white text-xs px-2 py-0.5 rounded">
                            📱 {item.deviceSerial}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      className="text-white text-opacity-60 hover:text-opacity-100 p-1"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Question */}
                  <div
                    onClick={() => handleUseQuestion(item.question)}
                    className="bg-white bg-opacity-10 rounded p-2 mb-2 cursor-pointer hover:bg-opacity-20 transition-all"
                  >
                    <p className="text-white text-opacity-50 text-xs font-bold mb-1">
                      QUESTION:
                    </p>
                    <p className="text-sm">
                      {truncateText(item.question, 150)}
                    </p>
                  </div>

                  {/* Answer */}
                  <div className="bg-white bg-opacity-5 rounded p-2">
                    <p className="text-white text-opacity-50 text-xs font-bold mb-1">
                      ANSWER:
                    </p>
                    <p className="text-sm text-white text-opacity-90">
                      {truncateText(item.answer, 200)}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUseQuestion(item.question);
                      }}
                      className="flex-1 bg-blue-500 bg-opacity-80 text-white text-xs px-3 py-1 rounded-xl hover:bg-opacity-100 transition-all"
                    >
                      ✨ Use this question
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShareToTeams(item);
                      }}
                      className="bg-purple-600 bg-opacity-80 text-white text-xs px-3 py-1 rounded-xl hover:bg-opacity-100 transition-all flex items-center gap-1"
                      title="Share to Microsoft Teams"
                    >
                      <ShareIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Teams Share Confirmation Dialog */}
      <TeamsShareConfirmDialog
        open={showTeamsDialog}
        onClose={() => setShowTeamsDialog(false)}
        onConfirm={handleTeamsDialogConfirm}
      />
    </>
  );
}
